import React, { useState, useEffect } from "react";
import { Link2 } from "lucide-react";
import {
  Trophy,
  Users,
  Calendar,
  MapPin,
  DollarSign,
  Gamepad2,
  Crown,
  Star,
  X,
} from "lucide-react";
import Alert from "../../../../components/alert";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import "../styles/tournament-details.css";
import Footer from "../../../../components/footer";
import Navbar from "../../../../components/navbar";
import GamingLoader from "../../../../components/gaming-loader";
import { useAuth } from "../../../../common/store/authStore";
import {
  deleteTournament,
  fetchTournamentById,
} from "../../../../common/services/tournament";
import {
  Tournament,
  TournamentStatus,
  TournamentTypeEnum,
} from "../../../../common/services/tournament.types";
import ChedGamingButton from "../../../../components/cheda-gaming-button";
import CustomAlert from "../../../../components/custom-alert";
import { FLW_PBLK, LOGO_URL } from "../../../../common/config/config";
import {
  joinTournament,
  joinFreeTournament,
} from "../../../../common/services/tournament";
import { useAlert } from "../../../../hooks/useAlert";
import { formatNumber } from "../../../../utils/formatNumber";
import { useTournaments } from "../../../../common/store/tournamentStore";

function TournamentDetailsSection() {
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const tournamentId = location.state?.tournamentId;
  const tournamentUrl = location.pathname.split('/').pop();
  const userCountry = location.state?.userCountry;

  const [showMessage, setShowMessage] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const { alert, showSuccess, showError, hideAlert } = useAlert();
  const { joinedTournaments, setJoinedTournaments } = useTournaments();

  const { authenticatedUser } = useAuth();
  const isLoggedIn = !!authenticatedUser;
  const currentUserId = authenticatedUser?.id;

  useEffect(() => {
    async function fetchTournamentData() {
      try {
        setLoading(true);
        const tournamentData = await fetchTournamentById(
          tournamentId || tournamentUrl,
          userCountry,
        );
        setTournament(tournamentData);
        document.title = `${tournamentData.tournamentName} - Cheda Gaming`;
      } catch {
        navigate("/tournaments");
      } finally {
        setLoading(false);
      }
    }
    fetchTournamentData();
  }, [tournamentId, tournamentUrl, userCountry, navigate]);

  const handleImageClick = () => setShowImageModal(true);
  const handleCloseModal = () => setShowImageModal(false);

  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowImageModal(false);
    }
  };
  const isHost = currentUserId === tournament?.host?._id;
  const isTournamentLive = tournament?.status === "live";
  const hasParticipant =
    Array.isArray(tournament?.participants) &&
    tournament?.participants.length > 0;
  const canDelete = isHost && !isTournamentLive && !hasParticipant;
  const isAlreadyRegistered = tournament?.participants?.includes(currentUserId);
  const tournamentLimit = tournament?.limit;
  const participantsCount = Array.isArray(tournament?.participants)
    ? tournament?.participants.length
    : 0;
  const isTournamentFull =
    tournamentLimit !== null &&
    tournamentLimit !== undefined &&
    tournamentLimit !== 0 &&
    Number(tournamentLimit) > 0 &&
    participantsCount >= Number(tournamentLimit);

  // Calculate fee with 10% processing fee
  const baseFee = Number(tournament?.convertedFee) || 0;
  const feeWithProcessing = baseFee + (baseFee * 0.05);

  const config = {
    public_key: FLW_PBLK,
    tx_ref: Date.now().toLocaleString(),
    amount: feeWithProcessing,
    currency: tournament?.displayCurrency,
    payment_options: "account, card, banktransfer, mobilemoney, ussd",
    customer: {
      email: authenticatedUser?.email,
      phone_number: authenticatedUser?.phoneNumber,
      name: `${authenticatedUser?.firstName} ${authenticatedUser?.lastName}`,
    },
    customizations: {
      title: "Tournament Registration",
      description: `Registration for ${tournament?.tournamentName}`,
      logo: LOGO_URL,
    },
  };

  const handleFlutterPayment = useFlutterwave(config);

  const confirmDeleteTournament = async () => {
    if (!tournament._id) return;
    try {
      setDeleteLoading(true);
      await deleteTournament(tournament._id);
      showSuccess("", "Tournament deleted successfully!");
      navigate("/dashboard");
    } catch (error: any) {
      showError("Error deleting tournament. Please try again.", error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewFixtures = () => {
    if (!isLoggedIn) {
      navigate("/login", {
        state: {
          from: `/fixtures/${tournament?.tournamentURL}`,
          message: "Please log in to view tournament fixtures.",
        },
      });
      return;
    }
    navigate(`/fixtures/${tournament?.tournamentURL}`, {
      state: { tournamentId: tournament?._id, userCountry },
    });
  };

  const handleJoinClick = () => {
    if (!isLoggedIn) {
      navigate("/login", {
        state: {
          from: `/tournaments/${tournament?.tournamentURL}`,
          message: "Please log in to join this tournament.",
        },
      });
      return;
    }

    if (isHost || isAlreadyRegistered || buttonLoading) return;

    setButtonLoading(true);

    if (tournament?.convertedFee === 0) {
      handleFreeTournament();
      return;
    }

    handleFlutterPayment({
      callback: (response) => {
        if (
          response.status === "completed" ||
          response.status === "successful" ||
          response.status === "success"
        ) {
          handlePaymentSuccess(response);
        } else {
          showError("Payment failed", "Please try again.");
        }
        closePaymentModal();
        setButtonLoading(false);
      },
      onClose: () => {
        setButtonLoading(false);
      },
    });
  };

  const handleFreeTournament = async () => {
    try {
      const baseFee = Number(tournament.convertedFee) || 0;
      const feeWithProcessing = baseFee + (baseFee * 0.1);
      
      await joinFreeTournament(
        tournament._id,
        formatNumber(feeWithProcessing),
        tournament.displayCurrency,
      );
      showSuccess("Success!", "Successfully registered for the tournament!");
      navigate("/dashboard");
    } catch (error) {
      showError("Error registering for tournament.", error.message);
    } finally {
      setButtonLoading(false);
    }
  };

  const handlePaymentSuccess = async (response: any) => {
    try {
      const baseFee = Number(tournament?.convertedFee) || 0;
      const feeWithProcessing = baseFee + (baseFee * 0.1);
      
      const data = await joinTournament(
        tournament._id,
        response.tx_ref,
        feeWithProcessing,
        tournament?.displayCurrency,
      );
      setJoinedTournaments([data, ...joinedTournaments]);
      showSuccess("Success!", "Successfully registered for the tournament!");
      navigate("/dashboard");
    } catch (error) {
      showError("Error registering for tournament.", error.message);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: tournament?.tournamentName,
      text: `Check out this tournament: ${tournament?.tournamentName}`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShowMessage(true);
        setTimeout(() => setShowMessage(false), 2000);
      }
    } catch {
      await navigator.clipboard.writeText(window.location.href);
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 2000);
    }
  };

  if (loading || !tournament) {
    return <GamingLoader message="Preparing your tournament experience..." />;
  }

  return (
    <>
      <Navbar />
      <div className="stadium-container">
        <div className="stadium-background">
          <div className="stadium-lights"></div>
          <div className="crowd-noise"></div>
          <div className="spotlight-effect"></div>
        </div>

        <div className="tournament-stadium-content">
          <div className="stadium-header">
            <div className="tournament-hero">
              <div
                className="tournament-image-container"
                onClick={handleImageClick}
              >
                <img
                  src={tournament?.tournamentImage}
                  alt={tournament.tournamentName}
                  className="tournament-hero-image"
                  style={{ cursor: "pointer" }}
                />
                <div className="image-overlay"></div>
                <div className="click-indicator">
                  <X style={{ transform: "rotate(45deg)" }} />
                  <span>Click to view full image</span>
                </div>
              </div>

              <div className="tournament-hero-content">
                <div className="tournament-badge">
                  <Crown className="crown-icon" />
                  <span>PREMIUM TOURNAMENT</span>
                </div>

                <h1 className="tournament-title">
                  {tournament?.tournamentName}
                </h1>

                <div className="tournament-meta">
                  <div className="meta-item">
                    <Calendar className="meta-icon" />
                    <span>{tournament?.convertedDateReadable}</span>
                  </div>
                  <div className="meta-item">
                    <MapPin className="meta-icon" />
                    <span>{tournament?.location}</span>
                  </div>
                  {isLoggedIn && isHost && (
                    <div className="meta-item">
                      <Users className="meta-icon" />
                      <span>
                        {tournament?.participants?.length || 0} Participant
                        {tournament?.participants.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="tournament-details-grid">
            <div className="tournament-info-section">
              <div className="info-card">
                <h3 className="card-title">
                  <Trophy className="card-icon" />
                  Prize Pool
                </h3>
                <div className="prize-amount">
                  <span className="currency">
                    {tournament?.displayCurrency}
                  </span>
                  <span className="amount">
                    {formatNumber(tournament?.convertedPrize)}
                  </span>
                </div>
              </div>

              <div className="info-card">
                <h3 className="card-title">
                  <Gamepad2 className="card-icon" />
                  Tournament Details
                </h3>
                <div className="details-list">
                  <div className="detail-item">
                    <span className="detail-label">Registration Fee:</span>
                    <span className="detail-value">
                      {tournament?.displayCurrency}{" "}
                      {formatNumber(tournament?.convertedFee)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Format:</span>
                    <span className="detail-value">
                      {tournament?.tournamentType ===
                      TournamentTypeEnum.SINGLE_ELIMINATION
                        ? "Single Elimination"
                        : "Double Elimination"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Game:</span>
                    <span className="detail-value">
                      {tournament?.selectedGame}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Platform:</span>
                    <span className="detail-value">{tournament?.platform}</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h3 className="card-title">
                  <Users className="card-icon" />
                  Tournament Description
                </h3>
                <p className="tournament-description">
                  {tournament?.description}
                </p>
              </div>
            </div>

            <div className="registration-section">
              <div className="registration-card">
                <div className="registration-header">
                  <h2>Join the Battle</h2>
                  <p>Ready to prove your skills?</p>
                </div>

                {isLoggedIn &&
                  isHost &&
                  tournament?.status === "upcoming" &&
                  (!tournament?.participants ||
                    tournament?.participants.length === 0) && (
                    <div className="host-actions">
                      <ChedGamingButton
                        children="Delete Tournament"
                        className="join-button delete-btn"
                        disabled={!canDelete}
                        onClick={() => setShowDeleteAlert(true)}
                      />
                    </div>
                  )}

                {(tournament.status !== TournamentStatus.UPCOMING || isHost) && (
                  <div className="tournament-live-section">
                    <div className="live-badge">
                      <Star className="live-icon" />
                      <span>
                        {tournament?.status === TournamentStatus.LIVE
                          ? "Tournament Live"
                          : tournament?.status === TournamentStatus.COMPLETED
                          ? "Tournament is Completed"
                          : isHost
                          ? "Manage Tournament"
                          : "Tournament Upcoming"}
                      </span>
                    </div>
                    <p>
                      {tournament?.status === TournamentStatus.COMPLETED
                        ? "This tournament has ended. View the final results and standings."
                        : tournament?.status === TournamentStatus.LIVE
                        ? "This tournament is currently live."
                        : isHost
                        ? "View and manage your tournament fixtures and participants."
                        : "This tournament is upcoming."}
                    </p>
                    <button
                      className="view-fixtures-btn"
                      onClick={handleViewFixtures}
                    >
                      <Calendar className="button-icon" />
                      {tournament.status === TournamentStatus.COMPLETED
                        ? "View Results"
                        : "View Fixtures"}
                    </button>
                  </div>
                )}

                {isAlreadyRegistered && (
                  <div className="already-registered">
                    <div className="registered-badge">
                      <Crown className="registered-icon" />
                      <span>Already Registered</span>
                    </div>
                    <p style={{ color: "white" }}>
                      You're all set for this tournament!
                    </p>
                  </div>
                )}

                {!isAlreadyRegistered && !isHost && tournament.status === TournamentStatus.UPCOMING && (
                  <div className="registration-actions">
                    <div className="fee-display">
                      <DollarSign className="fee-icon" />
                      <span className="fee-amount">
                        {tournament?.displayCurrency}{" "}
                        {formatNumber(tournament?.convertedFee)}
                      </span>
                      {tournament?.convertedFee === 0 && (
                        <span className="free-badge">FREE</span>
                      )}
                    </div>
                    {isTournamentFull ? (
                      <button
                        className={`join-button`}
                        disabled
                        style={{
                          background: "#ccc",
                          color: "#fff",
                          cursor: "not-allowed",
                        }}
                      >
                        <Gamepad2 className="button-icon" />
                        Tournament Full
                      </button>
                    ) : (
                      <>
                        <button
                          className={`join-button ${isHovered ? "hovered" : ""}`}
                          onMouseEnter={() => setIsHovered(true)}
                          onMouseLeave={() => setIsHovered(false)}
                          onClick={handleJoinClick}
                          disabled={buttonLoading}
                        >
                          {buttonLoading ? (
                            <div className="loading-spinner-small"></div>
                          ) : (
                            <>
                              <Gamepad2 className="button-icon" />
                              {tournament?.convertedFee === 0
                                ? "Join Free Tournament"
                                : "Pay & Join Tournament"}
                            </>
                          )}
                        </button>
                        <div className="registration-benefits">
                          <h4>What you get:</h4>
                          <ul>
                            <li>
                              🏆 Chance to win {tournament?.displayCurrency}{" "}
                              {formatNumber(tournament?.convertedPrize)}
                            </li>
                            <li>🎮 Competitive gameplay experience</li>
                            <li>📊 Tournament statistics and rankings</li>
                            <li>🏅 Achievement badges and recognition</li>
                          </ul>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="action-buttons">
                  <button
                    className="share-tournament-btn"
                    onClick={handleShare}
                  >
                    <Link2 className="button-icon" />
                    Share Tournament
                  </button>
                </div>

                {showMessage && (
                  <div className="success-message">
                    <Star className="message-icon" />
                    <span>Tournament link copied to clipboard!</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bottom-navigation">
            <Link
              to="/tournaments#join-tournaments-section"
              className="nav-link"
            >
              {/* <GrGallery className="nav-icon" /> */}
              <span>More Tournaments</span>
            </Link>
          </div>
        </div>

        {showImageModal && (
          <div
            className="image-modal-overlay"
            onClick={handleModalBackdropClick}
          >
            <div className="image-modal-content">
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <X />
              </button>
              <img
                src={tournament?.tournamentImage}
                alt={tournament?.tournamentName}
                className="modal-image"
              />
            </div>
          </div>
        )}
      </div>
      <Footer />
      <CustomAlert
        isOpen={showDeleteAlert}
        onClose={() => setShowDeleteAlert(false)}
        onConfirm={() => {
          confirmDeleteTournament();
        }}
        title="Delete Tournament"
        message="Are you sure you want to delete this tournament? This action cannot be undone and will remove all tournament data permanently."
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleteLoading}
      />

      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={hideAlert}
        position="top-right"
        duration={4000}
      />
    </>
  );
}

export default TournamentDetailsSection;
